import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { EmptyState, PageHeader, SkeletonTable } from "@/components/ui";
import { joinUtilityRows, latestTypedRejection } from "@/components/utilities/join-utility-rows";
import { RejectionTimelineCard } from "@/components/utilities/rejection-timeline-card";
import {
  TesterUtilitiesTable,
  type RequestError,
} from "@/components/utilities/tester-utilities-table";
import {
  useMyUtilityAccessRequest,
  myUtilityAccessErrorMessage,
} from "@/hooks/use-my-utility-access-request";
import { useFieldMe } from "@/hooks/use-persona";

export const Route = createFileRoute("/_company/utilities/")({
  component: UtilitiesPage,
});

/**
 * The tester's Utilities page: one row per water system joining the COMPANY's
 * status with the caller's own, rendered entirely from the field self-context
 * (`/field/me`) — `company_edges` × `authorizations`, joined on `org_id`
 * client-side. No second read exists: the two-route gate admission is what
 * lets a zero-grant roster member open this page at all, and their rows (the
 * company's edges, each Not requested) are the page's whole point — nothing is
 * granted automatically, so the ask itself is the surface.
 *
 * The company arm's own view of the same edges — the utility-code redemption
 * and the company's resubmit verbs — lives on the company workspace's Utility
 * Requests page and is untouched here.
 */
function UtilitiesPage() {
  const { t } = useTranslation("company");
  const fieldMe = useFieldMe();
  const request = useMyUtilityAccessRequest();
  // The refusal the table is quoting back, for the row that provoked it —
  // kept on the page so the table stays a pure function of rows + state.
  const [failed, setFailed] = useState<RequestError | null>(null);

  const rows = fieldMe.data
    ? joinUtilityRows(fieldMe.data.company_edges, fieldMe.data.authorizations)
    : [];
  const rejection = fieldMe.data ? latestTypedRejection(fieldMe.data.authorizations) : null;

  // The probe's own failure envelope: 403 is the only status that DENIES, and
  // a denial is a standing — the page's own empty state — never an error.
  const fieldStatus = (fieldMe.error as { status?: number } | null)?.status;
  const testerRefused = fieldStatus === 403;
  const testerUnreadable = fieldMe.isError && !testerRefused;

  const handleRequest = (orgId: string) => {
    setFailed(null);
    request.mutate(orgId, {
      onError: (error) => {
        const verdict = myUtilityAccessErrorMessage(error);
        setFailed({
          orgId,
          message: verdict.status
            ? t(verdict.key, {
                status: t(`company:utilities.request.statusLabels.${verdict.status}`),
              })
            : t(verdict.key),
        });
      },
    });
  };

  return (
    <div className="space-y-6 p-4 sm:p-8">
      <PageHeader title={t("company:utilities.title")} subtitle={t("company:utilities.subtitle")} />

      {/* The model in one breath, above the rows that prove it: the company's
          vetting never confers the person's access. */}
      <div
        role="note"
        className="flex gap-3 rounded-lg border border-blue-200 bg-blue-50 p-4 dark:border-blue-800 dark:bg-blue-900/30"
      >
        <div className="text-lg leading-none text-blue-600 dark:text-blue-300" aria-hidden="true">
          ℹ️
        </div>
        <div>
          <p className="text-sm font-semibold text-on-surface">
            {t("company:utilities.howAccessWorks.title")}
          </p>
          <p className="text-sm text-muted">{t("company:utilities.howAccessWorks.body")}</p>
        </div>
      </div>

      {testerUnreadable ? (
        <p role="status" className="text-sm text-muted">
          {t("company:utilities.loadError")}
        </p>
      ) : fieldMe.isPending ? (
        <SkeletonTable columns={5} rows={5} />
      ) : rows.length === 0 ? (
        <EmptyState
          title={t("company:utilities.empty.title")}
          description={t("company:utilities.empty.description")}
        />
      ) : (
        <>
          <TesterUtilitiesTable
            rows={rows}
            onRequest={handleRequest}
            requestingOrgId={request.isPending ? (request.variables ?? null) : null}
            requestError={failed}
          />
          {rejection !== null && <RejectionTimelineCard rejection={rejection} />}
        </>
      )}
    </div>
  );
}
